import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRight, Newspaper, ChevronLeft, ChevronRight, Package } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absImg = (src) => (!src ? null : (src.startsWith('http') ? src : `${BACKEND}${src}`));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export default function BlogListPage() {
  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/blog?page=${page}&limit=9`)
      .then((r) => { setArticles(r.data.articles || []); setPages(r.data.pages || 1); })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div data-testid="blog-list-page">
      <SEO
        title="Blog"
        description="Conseils pratiques pour écouler votre stock dormant, vendre vos invendus entre professionnels et gérer votre déstockage. Le blog DestockPro."
        path="/blog"
      />

      {/* Hero */}
      <section className="bg-[#1F3F72]" data-testid="blog-hero">
        <div className="site-container py-12 sm:py-16">
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#F25C05] mb-3">
            <Newspaper className="w-4 h-4" /> Le blog DestockPro
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white leading-tight max-w-3xl">
            Écouler son stock dormant, sans se tromper
          </h1>
          <p className="text-lg text-white/80 mt-4 max-w-2xl">
            Vendre, déstocker, gérer les invendus entre professionnels : nos guides concrets pour les entreprises.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="site-container">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-xl border border-neutral-100 overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-neutral-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-24 bg-neutral-100 rounded" />
                    <div className="h-5 w-full bg-neutral-100 rounded" />
                    <div className="h-4 w-3/4 bg-neutral-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16" data-testid="blog-empty">
              <Newspaper className="w-12 h-12 text-neutral-200 mx-auto mb-4" />
              <p className="text-neutral-500">Aucun article pour le moment. Revenez bientôt !</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {articles.map((a) => {
                const cover = absImg(a.cover_image);
                return (
                  <Link
                    key={a.id}
                    to={`/blog/${a.slug}`}
                    className="group flex flex-col rounded-xl border border-neutral-100 overflow-hidden bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                    data-testid={`blog-card-${a.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-[#1F3F72] flex items-center justify-center">
                      {cover ? (
                        <img src={cover} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <Package className="w-10 h-10 text-white/25" />
                      )}
                    </div>
                    <div className="flex flex-col flex-1 p-5">
                      <p className="flex items-center gap-1.5 text-xs text-neutral-400 mb-2">
                        <Calendar className="w-3.5 h-3.5" /> {fmtDate(a.published_at)}
                      </p>
                      <h2 className="font-heading text-lg font-semibold text-neutral-900 leading-snug mb-2 group-hover:text-[#1F3F72] transition-colors">
                        {a.title}
                      </h2>
                      <p className="text-sm text-neutral-500 leading-relaxed line-clamp-3 flex-1">{a.excerpt}</p>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-[#F25C05] mt-4">
                        Lire l'article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-12" data-testid="blog-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-neutral-500">Page {page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
